/**
 * ScorecardBody — the data-bound composition beneath the Scorecard page shell:
 * catalogue via useMetrics, ONE useResolve over the window (§5, site=all — the site
 * tab slices client-side), and the sections that compose below the matrix (drill S3,
 * trend A2). Loading/error/empty states are honest — a typed API error is a visible
 * message, never a blank grid. Split out of the page to keep it thin composition (CD-7).
 */

import React from 'react'
import { AlertCircle } from 'lucide-react'
import InfoBanner from '../components/InfoBanner'
import MetricMatrix from '../components/kit/MetricMatrix'
import DrillSection from '../components/DrillSection'
import TrendPanel from '../components/kit/TrendPanel'
import MatrixLegend from '../components/kit/MatrixLegend'
import { buildTrendSeries } from '../components/kit/trendSeries'
import { useMetrics } from '../hooks/useMetrics'
import { useResolve } from '../hooks/useResolve'
import { type Scope } from '../hooks/useScope'
import { useTrend, type Trend } from '../hooks/useTrend'
import type { CatalogueResponse, RegistryMetric, ResolveResponse } from '../types/metrics'

function LoadingState() {
  return (
    <div role="status" className="animate-pulse rounded-xl border border-line bg-panel px-4 py-12 text-center text-sm text-mut2">
      Loading scorecard…
    </div>
  )
}

function findMetric(catalogue: CatalogueResponse, metricId: string): RegistryMetric | null {
  for (const group of catalogue.pillars) {
    const metric = group.metrics.find((candidate) => candidate.id === metricId)
    if (metric) return metric
  }
  return null
}

/** The A2 trend chart for one metric, pivoted from the page's single /resolve (no
 * refetch — CD-7); title falls back to the catalogue title (the matrix's name) when
 * the metric serves nothing at the current site, never the raw registry id. */
function TrendSection({
  metricId,
  title,
  scope,
  resolve,
  onClose,
}: {
  metricId: string
  title?: string
  scope: Scope
  resolve: ResolveResponse
  onClose: () => void
}) {
  const series = buildTrendSeries(resolve, metricId, scope.site, title)
  return (
    <TrendPanel
      series={series}
      periodFrom={scope.periodFrom}
      periodTo={scope.periodTo}
      onClose={onClose}
    />
  )
}

function OverviewMatrix({ scope, catalogue, resolve, trend }: { scope: Scope; catalogue: CatalogueResponse; resolve: ResolveResponse; trend: Trend }) {
  return (
    <MetricMatrix
      pillars={catalogue.pillars}
      resolve={resolve}
      cadence={scope.cadence}
      site={scope.site}
      onDrill={scope.cadence === 'monthly' ? (cell) => scope.openDrill(cell.metric_id, cell.period) : undefined}
      onTrend={trend.openTrend}
    />
  )
}

function Composition({
  scope,
  trend,
  catalogue,
  resolve,
}: {
  scope: Scope
  trend: Trend
  catalogue: CatalogueResponse
  resolve: ResolveResponse
}) {
  return (
    <div className="space-y-4">
      <OverviewMatrix scope={scope} catalogue={catalogue} resolve={resolve} trend={trend} />
      <MatrixLegend resolve={resolve} site={scope.site} />
      {scope.drill && <DrillSection scope={scope} catalogue={catalogue} />}
      {trend.trend && (
        <TrendSection metricId={trend.trend} title={findMetric(catalogue, trend.trend)?.title} scope={scope} resolve={resolve} onClose={trend.closeTrend} />
      )}
    </div>
  )
}

export default function ScorecardBody({ scope }: { scope: Scope }): React.JSX.Element {
  const catalogue = useMetrics()
  const resolve = useResolve(scope.periodFrom, scope.periodTo, scope.cadence, 'all')
  const trend = useTrend()

  const error = resolve.error ?? catalogue.error
  if (error) {
    return (
      <InfoBanner
        variant="error"
        icon={AlertCircle}
        title="Scorecard unavailable"
        message={error.message}
      />
    )
  }
  if (!catalogue.data || !resolve.data) {
    return <LoadingState />
  }
  if (resolve.data.cells.length === 0) {
    return (
      <InfoBanner
        variant="info"
        message={`No cells served for ${scope.periodFrom} → ${scope.periodTo} (${scope.cadence}). The window may predate the served history.`}
      />
    )
  }
  return (
    <Composition
      scope={scope}
      trend={trend}
      catalogue={catalogue.data}
      resolve={resolve.data}
    />
  )
}
